import { useEffect, useState } from "react";

/** Per-browser QR scanner camera settings (each phone has different cameras,
 * so these live in localStorage, like the theme). */
export type Facing = "environment" | "user";
export type FocusMode = "continuous" | "manual";

export interface ScannerPrefs {
  /** Specific camera from enumerateDevices; "" = pick by `facing`. */
  deviceId: string;
  facing: Facing;
  /** Requested sensor height (ideal): 720 / 1080 / 1440. */
  resolution: number;
  /** Longest side of the frame sent for decoding. */
  maxSide: number;
  /** JPEG quality 0.4–0.95. */
  quality: number;
  /** Ms between frames sent for decoding. */
  intervalMs: number;
  /** Abandon a decode request after this many ms. */
  timeoutMs: number;
  torch: boolean;
  /** Zoom factor; 0 = camera default. Clamped to the camera's range at apply time. */
  zoom: number;
  focus: FocusMode;
  /** Manual focus distance as 0..1 of the camera's range (used when focus = manual). */
  focusDistance: number;
}

export const SCANNER_DEFAULTS: ScannerPrefs = {
  deviceId: "",
  facing: "environment",
  resolution: 1080,
  maxSide: 1280,
  quality: 0.6,
  intervalMs: 250,
  timeoutMs: 3000,
  torch: false,
  zoom: 0,
  focus: "continuous",
  focusDistance: 0.5,
};

export const RESOLUTIONS = [720, 1080, 1440] as const;
export const MAX_SIDES = [800, 960, 1280, 1600, 1920] as const;

const KEY = "app.scanner";
const EVENT = "app-scanner-prefs";

function clamp(v: unknown, min: number, max: number, fallback: number): number {
  const n = typeof v === "number" && Number.isFinite(v) ? v : fallback;
  return Math.min(max, Math.max(min, n));
}

function oneOf<T>(v: unknown, allowed: readonly T[], fallback: T): T {
  return (allowed as readonly unknown[]).includes(v) ? (v as T) : fallback;
}

/** Validates every field independently so one bad value never resets the rest. */
export function sanitizeScannerPrefs(raw: unknown): ScannerPrefs {
  const o = (raw && typeof raw === "object" ? raw : {}) as Record<string, unknown>;
  const d = SCANNER_DEFAULTS;
  return {
    deviceId: typeof o.deviceId === "string" ? o.deviceId : d.deviceId,
    facing: oneOf<Facing>(o.facing, ["environment", "user"], d.facing),
    resolution: oneOf<number>(o.resolution, RESOLUTIONS, d.resolution),
    maxSide: oneOf<number>(o.maxSide, MAX_SIDES, d.maxSide),
    quality: clamp(o.quality, 0.4, 0.95, d.quality),
    intervalMs: clamp(o.intervalMs, 100, 1000, d.intervalMs),
    timeoutMs: clamp(o.timeoutMs, 1000, 10000, d.timeoutMs),
    torch: typeof o.torch === "boolean" ? o.torch : d.torch,
    zoom: clamp(o.zoom, 0, 20, d.zoom),
    focus: oneOf<FocusMode>(o.focus, ["continuous", "manual"], d.focus),
    focusDistance: clamp(o.focusDistance, 0, 1, d.focusDistance),
  };
}

export function readScannerPrefs(): ScannerPrefs {
  try {
    const v = localStorage.getItem(KEY);
    return v ? sanitizeScannerPrefs(JSON.parse(v)) : SCANNER_DEFAULTS;
  } catch {
    return SCANNER_DEFAULTS;
  }
}

export function writeScannerPrefs(next: ScannerPrefs): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(next));
  } catch {
    /* private mode / blocked storage: still apply for this page */
  }
  window.dispatchEvent(new CustomEvent(EVENT, { detail: next }));
}

/** Live view of the scanner prefs: updates when changed in this tab or another. */
export function useScannerPrefs(): [ScannerPrefs, (patch: Partial<ScannerPrefs>) => void] {
  const [prefs, setPrefs] = useState<ScannerPrefs>(readScannerPrefs);
  useEffect(() => {
    const onLocal = (e: Event) => setPrefs((e as CustomEvent<ScannerPrefs>).detail);
    const onStorage = (e: StorageEvent) => {
      if (e.key === KEY) setPrefs(readScannerPrefs());
    };
    window.addEventListener(EVENT, onLocal);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener(EVENT, onLocal);
      window.removeEventListener("storage", onStorage);
    };
  }, []);
  const update = (patch: Partial<ScannerPrefs>) =>
    writeScannerPrefs(sanitizeScannerPrefs({ ...readScannerPrefs(), ...patch }));
  return [prefs, update];
}
